const MainSearch = class extends HTMLElement {
  constructor() {
    super();

    // Clicking close button
    this.querySelectorAll('.main-search__close').forEach((el) => {
      el.addEventListener('click', (evt) => {
        evt.preventDefault();
        document.body.classList.remove('show-search');
      });
    });

    // Pressing escape key
    this.querySelector('.main-search__input').addEventListener('keyup', (evt) => {
      if (evt.key === 'Escape') {
        this.querySelector('.main-search__close').dispatchEvent(new Event('click'));
      }
    });

    // Setup state
    this.activeTag = 'all';

    // Setup quick search
    if (this.dataset.quickSearch === 'true') {
      this.initQuickSearch();
    }

    // Tag filter clicks
    const tagButtons = this.querySelectorAll('.top-menu-sugg button');
    tagButtons.forEach((btn) => {
      btn.addEventListener('click', (evt) => {
        evt.preventDefault();

        // Update active state
        tagButtons.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');

        // Save active tag
        this.activeTag = btn.dataset.tag;

        // Trigger search refresh
        this.refreshQuickSearch();
      });
    });

    // Add tag to form submission
    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', (evt) => {
        // remove old hidden
        form.querySelectorAll('input[name="tag"]').forEach(el => el.remove());

        if (this.activeTag && this.activeTag !== 'all') {
          const hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'tag';
          hidden.value = this.activeTag;
          form.appendChild(hidden);
        }
      });
    }
  }

  initQuickSearch() {
    const searchInput = this.querySelector('.main-search__input');
    const searchTimeoutThrottle = 500;
    const resultLimit = 4;
    const includeMeta = this.dataset.quickSearchMeta === 'true';
    let searchTimeoutID = -1;
    let searchAbortController = null;

    const handleSearch = () => {
      const resultsBox = this.querySelector('.main-search__results');
      const valueToSearch = searchInput.value;
      const activeTag = this.activeTag || 'all';

      // Only search if text or filter applied
      if ((valueToSearch.length || activeTag !== 'all') && valueToSearch !== this.oldSearchValue + ':' + activeTag) {
        // Save previous
        this.oldSearchValue = valueToSearch + ':' + activeTag;

        // Kill outstanding ajax request
        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }
        clearTimeout(searchTimeoutID);

        // Build search URL
        const form = searchInput.closest('form');
        const linkURL = new URL(form.action);
        const formParams = new URLSearchParams(new FormData(form));
        formParams.forEach((value, key) => {
          linkURL.searchParams.set(key, value);
        });

        // Show loading
        this.classList.remove(
          'main-search--has-results',
          'main-search--results-on-multiple-lines',
          'main-search--no-results'
        );
        this.classList.add('main-search--loading');
        if (!this.querySelector('.main-search__results-spinner')) {
          resultsBox.innerHTML = '<div class="main-search__results-spinner"><div class="loading-spinner"></div></div>';
        }

        // Run ajax fetch
        searchTimeoutID = setTimeout(() => {
          searchAbortController = new AbortController();
          let ajaxUrl;

          if (theme.Shopify.features.predictiveSearch) {
            ajaxUrl = new URL(theme.routes.base + theme.routes.predictiveSearch);
            ajaxUrl.searchParams.set('q', valueToSearch);
            ajaxUrl.searchParams.set('section_id', 'predictive-search');
            ajaxUrl.searchParams.set('resources[limit]', 20);
            ajaxUrl.searchParams.set(
              'resources[options][fields]',
              'title,product_type,variants.title,vendor,variants.sku,variants.barcode'
            );

            // 👇 THIS enables SKU searching
            ajaxUrl.searchParams.set(
              'resources[options][query_by]',
              'title,product_type,variants.sku,variants.barcode'
            );
          } else {
            ajaxUrl = new URL(linkURL);
            ajaxUrl.searchParams.set('section_id', 'main-search');
          }

          fetch(ajaxUrl, { method: 'get', signal: searchAbortController.signal })
            .then((response) => {
              if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
              return response.text();
            })
            .then((response) => {
              const template = document.createElement('template');
              template.innerHTML = response;
              let resultsList = null;
              if (theme.Shopify.features.predictiveSearch) {
                resultsList = template.content.querySelector('.product-grid');
              } else {
                resultsList = template.content.querySelector('.section-search-template .product-grid');
              }

              // reset
              searchAbortController = null;
              this.classList.remove(
                'main-search--has-results',
                'main-search--results-on-multiple-lines',
                'main-search--no-results'
              );
              resultsBox.innerHTML = '';

              const resultsProducts = document.createElement('div');
              resultsProducts.className = 'main-search__results__products collection-listing';
              resultsProducts.innerHTML = '<div></div>';
              const resultsPages = document.createElement('div');
              resultsPages.className = 'main-search__results__pages';

              resultsProducts.firstElementChild.className = resultsList.className;

              // Convert blocks + filter by tag
              let shownCount = 0;
              resultsList.querySelectorAll('.product-block:not(.collection-block):not(.page-block)').forEach((block) => {
                let include = true;

                if (activeTag !== 'all') {
                  const tags = block.dataset.tags ? block.dataset.tags.split(',') : [];
                  include = tags.map((t) => t.trim().toLowerCase()).includes(activeTag.toLowerCase());
                }

                if (include && shownCount < resultLimit) {
                  block.classList.add('main-search-result');
                  block.querySelectorAll('.btn.quickbuy-toggle').forEach((el) => el.remove());
                  block.querySelectorAll('.quickbuy-toggle').forEach((el) => el.classList.remove('quickbuy-toggle'));
                  resultsProducts.firstElementChild.appendChild(block);
                  shownCount++;
                }
              });

              // Pages untouched
              resultsList.querySelectorAll('.product-block.page-block').forEach((block) => {
                const item = document.createElement('a');
                item.className = 'main-search-result main-search-result--page';
                item.href = block.querySelector('a').href;
                item.innerHTML = '<div class="main-search-result__text"></div>';
                item.firstElementChild.innerText = block.querySelector('.page-block__title').innerText;
                resultsPages.appendChild(item);
              });

              this.classList.remove('main-search--loading');

              const areProducts = !!resultsProducts.querySelector('.main-search-result');
              const arePages = !!resultsPages.querySelector('.main-search-result');

              if (areProducts || arePages) {
                this.classList.add('main-search--has-results');
                this.classList.toggle(
                  'main-search--results-on-multiple-lines',
                  resultsProducts.querySelectorAll('.product-block').length > 4
                );
                if (areProducts) resultsBox.appendChild(resultsProducts);
                if (arePages) {
                  const heading = document.createElement('h6');
                  heading.className = 'main-search-result__heading';
                  heading.innerHTML = theme.strings.generalSearchPages;
                  resultsPages.insertAdjacentElement('afterbegin', heading);
                  resultsBox.appendChild(resultsPages);
                }

                const allLink = document.createElement('a');
                allLink.className = 'main-search__results-all-link btn btn--secondary';

                // Build URL same as form submission
                const allUrl = new URL(linkURL);
                if (activeTag && activeTag !== 'all') {
                  allUrl.searchParams.set('tag', activeTag);
                }
                allLink.href = allUrl.toString();

                allLink.innerHTML = theme.strings.generalSearchViewAll;
                resultsBox.appendChild(allLink);
                
              } else {
                this.classList.add('main-search--no-results');
                const emptyMessage = document.createElement('div');
                emptyMessage.className = 'main-search__empty-message';
                emptyMessage.innerHTML = theme.strings.generalSearchNoResultsWithoutTerms;
                resultsBox.appendChild(emptyMessage);
              }
            })
            .catch((error) => {
              console.warn(error);
            });
        }, searchTimeoutThrottle);
      } else if (!valueToSearch.length && activeTag === 'all') {
        // Reset if no search and no filter
        this.oldSearchValue = '';
        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }
        clearTimeout(searchTimeoutID);
        this.classList.remove(
          'main-search--has-results',
          'main-search--results-on-multiple-lines',
          'main-search--loading'
        );
        this.querySelector('.main-search__results').innerHTML = '';
      }
    };

    this.handleSearch = handleSearch;
    searchInput.addEventListener('keyup', handleSearch.bind(this));
    // this event comment because this is affect on loadinf, double loading
    // searchInput.addEventListener('change', handleSearch.bind(this));
  }

  refreshQuickSearch() {
    if (this.handleSearch) {
      this.handleSearch();
    }
  }
};

window.customElements.define('main-search', MainSearch);
